import React, { createContext, useContext } from 'react';

import type { CodingScreenConfig } from '../config';
import type { UiEventBus } from '../events/uiEventBus';
import type { CodingScreenApi } from './api';
import type { PanelContent } from './panelContent';
import type { CodingScreenState } from './state';

/**
 * The value every `project_starburst` component reads from context: `config` is
 * REQUIRED (the shell always renders from one), `state`/`api`/`panelContent` are OPTIONAL -- no
 * runtime placeholders. A dashboard-ui-side adapter (living OUTSIDE this folder, so it may see
 * redux/Apollo) injects real values; `project_starburst` itself never reads redux/Apollo directly.
 */
export interface CodingScreenContextValue {
  config: CodingScreenConfig;
  state?: CodingScreenState;
  api?: CodingScreenApi;
  panelContent?: PanelContent;
  bus?: UiEventBus;
}

const Ctx = createContext<CodingScreenContextValue | null>(null);

/** Requires `config`; `state`/`api` are optional props and are `undefined` when omitted -- the
 *  shell (a later task) omits them today, per the file header above. */
export const CodingScreenProvider: React.FC<
  React.PropsWithChildren<CodingScreenContextValue>
> = ({ config, state, api, panelContent, bus, children }) => (
  <Ctx.Provider value={{ config, state, api, panelContent, bus }}>
    {children}
  </Ctx.Provider>
);

/** Throws a clear, actionable error at the exact call site instead of silently returning `null`
 *  when a component renders outside `CodingScreenProvider`. */
export function useCodingScreen(): CodingScreenContextValue {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error('useCodingScreen must be used within CodingScreenProvider');
  }
  return v;
}

/** Convenience accessor for the one value every consumer needs -- the config. */
export const useCodingConfig = (): CodingScreenConfig =>
  useCodingScreen().config;
